import React from 'react';
import { GraduationCap, BookOpen, Briefcase, Zap, Globe, Car, Star } from 'lucide-react';
import { labelOf } from '../constants/jobOptions';
import {
  SCHOOL_STATUS_LABELS, TRAINING_STATUS_LABELS, EXPERIENCE_TYPE_LABELS,
  LANGUAGE_LEVEL_LABELS, DRIVERS_LICENSE_LABELS,
} from '../constants/profileOptions';
import './ApplicantProfile.css';

const list = (v) => (Array.isArray(v) ? v : []);
const formatGrade = (g) => Number(g).toFixed(1).replace('.', ',');

function Row({ Icon, title, children }) {
  return (
    <div className="applicant-profile__row">
      <div className="applicant-profile__label"><Icon size={14} /> {title}</div>
      <div className="applicant-profile__value">{children}</div>
    </div>
  );
}

// "Erzähl uns von dir" + "Über mich", so wie der Arbeitgeber es bei der
// Bewerbung sieht. Leere Angaben werden ausgelassen, die Note nur, wenn es eine gibt.
export default function ApplicantProfile({ profile, showBio = true }) {
  const p = profile || {};
  const experience = list(p.experience);
  const skills = list(p.skills);
  const languages = list(p.languages);
  const hasGrade = p.school_grade !== null && p.school_grade !== undefined && p.school_grade !== '';

  const schoolDetails = [
    p.school_name,
    p.school_status === 'schueler'
      ? p.school_class && `Klasse ${p.school_class}`
      : p.school_graduation_year && `Abschluss ${p.school_graduation_year}`,
    hasGrade && `Note ${formatGrade(p.school_grade)}`,
  ].filter(Boolean).join(' · ');

  const isEmpty = !p.school_status && !p.training_status && !experience.length && !skills.length
    && !languages.length && !p.drivers_license && !(showBio && p.bio);
  if (isEmpty) return <p className="applicant-profile__empty">Noch keine Angaben.</p>;

  return (
    <div className="applicant-profile">
      {p.school_status && (
        <Row Icon={GraduationCap} title="Schule">
          <strong>{labelOf(SCHOOL_STATUS_LABELS, p.school_status)}</strong>
          {schoolDetails && <span className="applicant-profile__sub">{schoolDetails}</span>}
        </Row>
      )}

      {p.training_status && (
        <Row Icon={BookOpen} title="Ausbildung / Studium">
          <strong>{labelOf(TRAINING_STATUS_LABELS, p.training_status)}</strong>
          {p.training_status !== 'keins' && p.training_field && (
            <span className="applicant-profile__sub">{p.training_field}</span>
          )}
        </Row>
      )}

      {experience.length > 0 && (
        <Row Icon={Briefcase} title="Erfahrung">
          <ul className="applicant-profile__experience">
            {experience.map((e, i) => (
              <li key={i}>
                <span className="applicant-profile__type">{labelOf(EXPERIENCE_TYPE_LABELS, e.type)}</span>
                {/* Vor der Einladung liefert der Server keine Firma/Organisation */}
                {e.organization && <strong>{e.organization}</strong>}
                {e.period && <span className="applicant-profile__sub">{e.period}</span>}
                {e.description && <span className="applicant-profile__desc">{e.description}</span>}
              </li>
            ))}
          </ul>
        </Row>
      )}

      {skills.length > 0 && (
        <Row Icon={Zap} title="Skills">
          <div className="applicant-profile__tags">
            {skills.map(s => <span key={s} className="applicant-profile__tag">{s}</span>)}
          </div>
        </Row>
      )}

      {languages.length > 0 && (
        <Row Icon={Globe} title="Sprachen">
          {languages.map(l => `${l.language} (${labelOf(LANGUAGE_LEVEL_LABELS, l.level)})`).join(', ')}
        </Row>
      )}

      {p.drivers_license && (
        <Row Icon={Car} title="Führerschein">
          {labelOf(DRIVERS_LICENSE_LABELS, p.drivers_license)}
        </Row>
      )}

      {showBio && p.bio && (
        <Row Icon={Star} title="Über mich">
          <p className="applicant-profile__bio">{p.bio}</p>
        </Row>
      )}
    </div>
  );
}
